import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useCalculatorEnabled } from "@/hooks/use-calculator-enabled";

const navItems = [
  { label: "Home", to: "/" },
  { label: "Products", to: "/products" },
  { label: "About", to: "/about" },
  { label: "Quality", to: "/quality" },
  { label: "Calculator", to: "/calculator" },
  { label: "Membership", to: "/membership" },
  { label: "Contact", to: "/contact" },
];

export const PublicHeader = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const calcEnabled = useCalculatorEnabled();
  const items = navItems.filter((i) => i.to !== "/calculator" || calcEnabled);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const dashHref = user ? (user.role === "admin" ? "/admin" : "/member") : "/login";

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b transition-shadow ${
        scrolled ? "border-border bg-white/90 shadow-sm backdrop-blur" : "border-transparent bg-white"
      }`}
    >
      <div className="pg-container px-4 sm:px-6 lg:px-8">
        {/* Single row: wordmark left, primary navigation + account actions right (desktop) / menu button (mobile) */}
        <div className="flex h-[72px] items-center justify-between gap-4 xl:gap-6">
          <div data-testid="header-logo" className="shrink-0">
            <Logo size="text-[22px] min-[400px]:text-[24px] sm:text-[22px] xl:text-[24px]" taglineClassName="min-[400px]:text-[8.5px] lg:hidden xl:block" />
          </div>
          <nav className="hidden items-center gap-1 lg:mt-2 lg:flex" aria-label="Primary">
            {items.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`nav-link-${item.label.toLowerCase()}`}
                className={`rounded-lg px-2 py-2 text-[13px] font-medium transition-colors hover:bg-slate-100 xl:px-3.5 xl:text-sm ${
                  location.pathname === item.to ? "text-brand-blue" : "text-navy/80"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
          <div className="hidden shrink-0 items-center gap-2 lg:mt-2 lg:flex">
            {user ? (
              <Button data-testid="header-dashboard-button" onClick={() => navigate(dashHref)} className="bg-navy text-white hover:bg-navy/90">
                My Dashboard
              </Button>
            ) : (
              <>
                <Button data-testid="header-login-button" variant="ghost" onClick={() => navigate("/login")} className="text-navy hover:bg-slate-100">
                  Login
                </Button>
                <Button data-testid="header-apply-button" onClick={() => navigate("/membership")} className="bg-navy text-white hover:bg-navy/90">
                  Apply for Access
                </Button>
              </>
            )}
          </div>
          <button className="lg:hidden" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="mobile-menu-toggle">
            {open ? <X className="h-6 w-6 text-navy" /> : <Menu className="h-6 w-6 text-navy" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="border-t border-border bg-white px-4 py-3 lg:hidden">
          <nav className="flex flex-col gap-1">
            {items.map((item) => (
              <Link key={item.to} to={item.to} className="rounded-lg px-3 py-2.5 text-sm font-medium text-navy hover:bg-slate-100">
                {item.label}
              </Link>
            ))}
            <div className="mt-2 flex flex-col gap-2">
              {user ? (
                <Button onClick={() => navigate(dashHref)} className="bg-navy text-white">My Dashboard</Button>
              ) : (
                <>
                  <Button variant="outline" onClick={() => navigate("/login")}>Login</Button>
                  <Button onClick={() => navigate("/membership")} className="bg-navy text-white">Apply for Access</Button>
                </>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};
